import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, X, Target, AlertTriangle } from 'lucide-react';
import { socialService } from '../../services/socialService';
import api from '../../services/api';

interface InterestOption {
  id: string;
  name: string;
  audience_size?: number;
  path?: string[];
  type?: string;
}

export interface MetaInterestItem {
  id: string;
  name: string;
}

interface MetaInterestsInputProps {
  // Armazena {id, name} pra backend não precisar re-pesquisar o catálogo da Meta
  // no push (re-busca era flaky: nomes com múltiplos matches ou zero resultado
  // faziam interesses sumirem silenciosamente). Aceita strings legacy também.
  values: Array<MetaInterestItem | string>;
  onChange: (values: MetaInterestItem[]) => void;
  placeholder?: string;
  max?: number;
}

// Input com autocomplete contra o catálogo da Meta Graph API (interest/behavior/demographic).
// Substitui o CommaSeparatedInput pra garantir que só valores válidos entram — Meta
// rejeita strings livres, precisa de item do catálogo oficial.
const MetaInterestsInput: React.FC<MetaInterestsInputProps> = ({
  values,
  onChange,
  placeholder,
  max = 20,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<InterestOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Normaliza strings legacy pra objetos uniformes; mantém IDs quando já são objetos
  const normalized: MetaInterestItem[] = values.map(v =>
    typeof v === 'string' ? { id: '', name: v } : v
  );

  // Validação upfront: consulta Meta (debounced) pra marcar quais interesses
  // estão deprecated ANTES do save, em vez de descobrir só quando o push falha.
  const [deprecatedIds, setDeprecatedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const withIds = normalized.filter(i => i.id);
    if (withIds.length === 0) {
      setDeprecatedIds(new Set());
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/campaigns/targeting/meta/validate-interests', { interests: withIds });
        const dep = r.data?.data?.deprecated || [];
        setDeprecatedIds(new Set(dep.map((d: MetaInterestItem) => d.id)));
      } catch {
        // Silencioso — se validação falhar, não bloqueia (retry-on-push cobre)
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await socialService.searchBoostInterests(query);
        if (response.success && Array.isArray(response.data)) {
          // Filtra itens já selecionados (compara por nome, case-insensitive)
          const existingNames = new Set(normalized.map(v => v.name.toLowerCase()));
          setResults(response.data.filter(r => !existingNames.has(r.name.toLowerCase())));
        }
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query, values]);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const add = (item: InterestOption) => {
    if (normalized.length >= max) return;
    if (normalized.some(v => v.name.toLowerCase() === item.name.toLowerCase())) return;
    onChange([...normalized, { id: item.id, name: item.name }]);
    setQuery('');
    setResults([]);
  };

  const remove = (name: string) => {
    onChange(normalized.filter(v => v.name !== name));
  };

  const formatAudience = (n?: number) => {
    if (!n) return '';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return String(n);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || t('campaigns.interests.searchPlaceholder', 'Buscar interesses, comportamentos, dados demográficos...')}
          disabled={normalized.length >= max}
          className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
          {results.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => add(item)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between border-b border-gray-100 dark:border-gray-700/50 last:border-0"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Target className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                  <span className="text-gray-900 dark:text-white truncate">{item.name}</span>
                  {item.type && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">
                      {item.type}
                    </span>
                  )}
                </div>
                {item.path && item.path.length > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 ml-5 mt-0.5 truncate">
                    {item.path.join(' › ')}
                  </p>
                )}
              </div>
              {item.audience_size != null && (
                <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                  {formatAudience(item.audience_size)} {t('campaigns.interests.people', 'pessoas')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {isOpen && query.length >= 2 && !searching && results.length === 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
          {t('campaigns.interests.noResults', 'Nenhum interesse encontrado no catálogo da Meta pra "{{query}}"', { query })}
        </div>
      )}

      {normalized.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {normalized.map((item) => {
              const isDeprecated = item.id && deprecatedIds.has(item.id);
              return (
                <span
                  key={item.name}
                  title={isDeprecated ? t('campaigns.interests.deprecatedTooltip', 'Descontinuado pela Meta — será removido automaticamente no save') : undefined}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                    isDeprecated
                      ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                      : 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  }`}
                >
                  {isDeprecated && <AlertTriangle className="w-3 h-3" />}
                  {item.name}
                  <button type="button" onClick={() => remove(item.name)} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
          {deprecatedIds.size > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {t('campaigns.interests.deprecatedWarning',
                '{{count}} interesse(s) descontinuado(s) pela Meta. Remova manualmente ou deixe que a gente remove no save.',
                { count: deprecatedIds.size })}
            </p>
          )}
        </>
      )}

      {normalized.length >= max && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          {t('campaigns.interests.maxReached', 'Máximo de {{max}} interesses atingido', { max })}
        </p>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {t('campaigns.interests.hint', 'Digite pra buscar no catálogo oficial da Meta. Meta só aceita itens da lista — digitação livre não funciona.')}
      </p>
    </div>
  );
};

export default MetaInterestsInput;
